/**
 * PlanetDragOverlay - Time navigation UI for planet drag interaction
 *
 * Renders inside SVG foreignObject in chart-center for automatic positioning during resize.
 *
 * Shows:
 * - Loading spinner while buffer is loading
 * - Navigation buttons < [time] > when ready
 * - Click: ±1 degree, Shift+Click: ±10 degrees
 * - Hold: auto-repeat
 */

import { devLog } from '../../../utils/devLog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { globalEventStream, createEvent } from './universal_architecture/core/EventStream';
import { timeNavigationConfig } from '../../../config/timeNavigationConfig';
import { formatLocalDegrees } from './Planets/planetFormatters';
import { RASHI_NAMES } from './universal_architecture/config/PlanetLayerConfig';
import {
  NavButton,
  BufferIndicator,
  ActionButtons,
  NavigationHint,
  formatTime,
  formatDate,
  formatDegreeOffset,
  SHIFT_MULTIPLIER,
} from './MotionOverlayComponents';
import type { BaseEvent, EventSubscription } from './universal_architecture/types/EventTypes';
import { setActivePlanetDrag, clearActiveMotion } from './shared/motionState';
import './PlanetDragOverlay.css';

const DEBUG = false;

// Buffer size for degree motion (721 points: 360 on each side + center)
const DEGREE_BUFFER_SIZE = 721;
const DEGREE_CENTER_INDEX = 360;

// =============================================================================
// Types
// =============================================================================

interface PlanetDragOverlayProps {
  chartId: string;
}

interface PlanetPosition {
  longitude: number;
  rashiIndex: number;
  degreesInRashi: number;
}

type OverlayState =
  | { type: 'hidden' }
  | { type: 'loading'; planet: string; progress: number }
  | {
      type: 'navigating';
      planet: string;
      currentTime: Date;
      currentIndex: number;
      centerIndex: number;
      degreeOffset: number;
      planetPosition: PlanetPosition | null;
      canGoBack: boolean;
      canGoForward: boolean;
      stepDegrees: number;
      timezoneCorrection: number;
      isPartial?: boolean;
      loadingProgress?: number;
      originalLongitude: number | null;
    };

// =============================================================================
// Component
// =============================================================================

export const PlanetDragOverlay: React.FC<PlanetDragOverlayProps> = ({ chartId }) => {
  const [state, setState] = useState<OverlayState>({ type: 'hidden' });
  const [waitingDirection, setWaitingDirection] = useState<'back' | 'forward' | null>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isShiftPressedRef = useRef(false);

  // ===========================================================================
  // Portal target - find foreignObject container in SVG
  // ===========================================================================

  useEffect(() => {
    // Try to find the portal target element
    const findPortalTarget = () => {
      const targetId = `chart-center-overlay-${chartId}`;
      const target = document.getElementById(targetId);
      if (target && target !== portalTarget) {
        setPortalTarget(target);
        if (DEBUG) devLog(`[PlanetDrag:Overlay] Found portal target: #${targetId}`);
      }
    };

    // Initial search
    findPortalTarget();

    // Also search when DOM changes (chart might be created later)
    const observer = new MutationObserver(() => {
      if (!portalTarget) {
        findPortalTarget();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [chartId, portalTarget]);

  // ===========================================================================
  // Keyboard tracking for Shift
  // ===========================================================================

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') isShiftPressedRef.current = true;
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') isShiftPressedRef.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // ===========================================================================
  // Navigation
  // ===========================================================================

  const navigate = useCallback((direction: 'back' | 'forward') => {
    if (state.type !== 'navigating') return;

    // Simple index-based navigation: ±1 (normal), ±10 (with Shift)
    const steps = isShiftPressedRef.current ? timeNavigationConfig.shiftMultiplier : 1;
    const indexDelta = direction === 'forward' ? steps : -steps;

    if (DEBUG) devLog(`[PlanetDrag:Overlay] Navigate ${direction} by ${indexDelta} index(es)`);

    // Emit navigation event with index delta
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.navigate',
      'PlanetDragOverlay',
      {
        metadata: {
          chartId,
          planet: state.planet,
          indexDelta,
        },
      }
    ));
  }, [chartId, state]);

  const handleConfirm = useCallback(() => {
    if (state.type !== 'navigating') return;

    // Preserve full precision (backend handles microseconds)
    const selectedTimeISO = state.currentTime.toISOString();

    if (DEBUG) devLog(`[PlanetDrag:Overlay] Confirm at ${selectedTimeISO}`);

    // Emit end event with selected time
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.end',
      'PlanetDragOverlay',
      {
        metadata: {
          chartId,
          planet: state.planet,
          selectedTime: selectedTimeISO,
        },
      }
    ));

    setWaitingDirection(null);
    setState({ type: 'hidden' });

    // Emit overlay closed event
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.overlay.closed',
      'PlanetDragOverlay',
      { metadata: { chartId } }
    ));
  }, [chartId, state]);

  const handleCancel = useCallback(() => {
    if (DEBUG) devLog(`[PlanetDrag:Overlay] Cancel`);

    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.cancel',
      'PlanetDragOverlay',
      { metadata: { chartId } }
    ));

    setWaitingDirection(null);
    setState({ type: 'hidden' });

    // Emit overlay closed event
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.overlay.closed',
      'PlanetDragOverlay',
      { metadata: { chartId } }
    ));
  }, [chartId]);

  // ===========================================================================
  // Hold-to-repeat
  // ===========================================================================

  const startHold = useCallback((direction: 'back' | 'forward') => {
    // Clear any existing timers
    stopHold();

    // Initial navigation
    navigate(direction);

    // Start repeat after delay
    holdTimerRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        navigate(direction);
      }, timeNavigationConfig.holdRepeatIntervalMs);
    }, timeNavigationConfig.holdRepeatDelayMs);
  }, [navigate]);

  const stopHold = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  }, []);

  // Handle click on disabled button during partial loading
  const handleDisabledClick = useCallback((direction: 'back' | 'forward') => {
    if (state.type !== 'navigating') return;
    if (!state.isPartial) return; // Only show waiting state during loading

    if (DEBUG) devLog(`[PlanetDrag:Overlay] Waiting for ${direction} data...`);
    setWaitingDirection(direction);
  }, [state]);

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  const handleDragStart = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    const planet = event.metadata.planet as string;
    if (DEBUG) devLog(`[PlanetDrag:Overlay] Drag start: ${planet}`);

    // Cancel any active time motion first
    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.cancel',
      'PlanetDragOverlay',
      { metadata: { chartId } }
    ));
    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.overlay.closed',
      'PlanetDragOverlay',
      { metadata: { chartId } }
    ));

    setState({ type: 'loading', planet, progress: 0 });

    // Set shared state for late-mounting components
    setActivePlanetDrag(chartId, planet);

    // Emit overlay opened event
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.overlay.opened',
      'PlanetDragOverlay',
      { metadata: { chartId, planet } }
    ));
  }, [chartId]);

  const handleDragProgress = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    const progress = event.metadata.progress as number;
    setState(prev => {
      if (prev.type === 'loading') {
        return { ...prev, progress };
      }
      // Also update progress if we're navigating with partial data
      if (prev.type === 'navigating' && prev.isPartial) {
        const isComplete = progress >= 100;
        return {
          ...prev,
          loadingProgress: progress,
          isPartial: !isComplete,
        };
      }
      return prev;
    });
  }, [chartId]);

  const handleDragReady = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    const planet = event.metadata.planet as string;
    const centerIndex = event.metadata.centerIndex as number;
    const timestamp = event.metadata.timestamp as string;
    const planetPosition = event.metadata.planetPosition as PlanetPosition | null;
    const canGoBack = event.metadata.canGoBack as boolean;
    const canGoForward = event.metadata.canGoForward as boolean;
    const stepDegrees = (event.metadata.stepDegrees as number) || timeNavigationConfig.defaultStepDegrees;
    const isPartial = event.metadata.isPartial as boolean || false;
    const progress = (event.metadata.progress as number) || 100;

    // Calculate timezone correction (chart timezone vs API timezone)
    const chartTimezoneOffset = (event.metadata.chartTimezoneOffset as number) || 0;
    const apiUtcOffset = (event.metadata.apiUtcOffset as number) || 0;
    const timezoneCorrection = chartTimezoneOffset - apiUtcOffset; // hours difference

    if (DEBUG) {
      devLog(`[PlanetDrag:Overlay] Ready: ${planet}, step: ${stepDegrees}°, time: ${timestamp}, partial: ${isPartial}, progress: ${progress}%`);
      // Log position comparison for debugging
      const overlayLongitude = planetPosition?.longitude;
      const chartOriginalLongitude = event.metadata.originalLongitude as number | null | undefined;
      // Use != null to check for both null and undefined
      if (overlayLongitude != null && chartOriginalLongitude != null) {
        const diff = Math.abs(overlayLongitude - chartOriginalLongitude);
        devLog(`[PlanetDrag:Overlay] Position check: chart=${chartOriginalLongitude.toFixed(6)}°, overlay=${overlayLongitude.toFixed(6)}°, diff=${diff.toFixed(6)}°`);
        if (diff > 0.001) {
          devLog(`[PlanetDrag:Overlay] ⚠️ Position mismatch detected! diff=${diff.toFixed(4)}° (${(diff * 60).toFixed(2)} arcmin)`);
        }
      }
    }

    // Clear waiting direction if that direction is now available
    setWaitingDirection(prev => {
      if (prev === 'back' && canGoBack) return null;
      if (prev === 'forward' && canGoForward) return null;
      return prev;
    });
    // Use degreeOffset from event if provided (step change preserves offset), otherwise 0
    const eventDegreeOffset = (event.metadata.degreeOffset as number) ?? 0;
    // Original longitude for Apply button state
    const originalLongitude = (event.metadata.originalLongitude as number) ?? null;

    setState({
      type: 'navigating',
      planet,
      currentTime: new Date(timestamp),
      currentIndex: centerIndex,
      centerIndex,
      degreeOffset: eventDegreeOffset,
      planetPosition,
      canGoBack,
      canGoForward,
      stepDegrees,
      timezoneCorrection,
      isPartial,
      loadingProgress: progress,
      originalLongitude,
    });
  }, [chartId]);

  const handleNavigationUpdate = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    const newIndex = event.metadata.newIndex as number;
    const centerIndex = event.metadata.centerIndex as number | undefined;
    const degreeOffset = event.metadata.degreeOffset as number;
    const timestamp = event.metadata.timestamp as string;
    const planetPosition = event.metadata.planetPosition as PlanetPosition | null;
    const canGoBack = event.metadata.canGoBack as boolean;
    const canGoForward = event.metadata.canGoForward as boolean;
    const isPartial = event.metadata.isPartial as boolean | undefined;
    const originalLongitude = event.metadata.originalLongitude as number | null | undefined;

    // Clear waiting direction if that direction is now available
    setWaitingDirection(prev => {
      if (prev === 'back' && canGoBack) return null;
      if (prev === 'forward' && canGoForward) return null;
      return prev;
    });

    setState(prev => {
      if (prev.type !== 'navigating') return prev;
      return {
        ...prev,
        currentIndex: newIndex,
        centerIndex: centerIndex ?? prev.centerIndex,  // Update centerIndex for BufferIndicator during progressive loading
        degreeOffset: degreeOffset ?? prev.degreeOffset,
        currentTime: new Date(timestamp),
        // Only update planetPosition if provided (null means keep current)
        planetPosition: planetPosition !== null ? planetPosition : prev.planetPosition,
        canGoBack,
        canGoForward,
        // Update isPartial if provided
        isPartial: isPartial !== undefined ? isPartial : prev.isPartial,
        // Keep originalLongitude (only update if explicitly provided)
        originalLongitude: originalLongitude !== undefined ? originalLongitude : prev.originalLongitude,
      };
    });
  }, [chartId]);

  const handleDragEnd = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;
    stopHold();
    setWaitingDirection(null);
    setState({ type: 'hidden' });
    clearActiveMotion(chartId);
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.overlay.closed',
      'PlanetDragOverlay',
      { metadata: { chartId } }
    ));
  }, [chartId, stopHold]);

  const handleDragCancel = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;
    stopHold();
    setWaitingDirection(null);
    setState({ type: 'hidden' });
    clearActiveMotion(chartId);
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.overlay.closed',
      'PlanetDragOverlay',
      { metadata: { chartId } }
    ));
  }, [chartId, stopHold]);

  // Close overlay when another chart is activated (new chart, import, tab switch)
  const handleChartActivated = useCallback((event: BaseEvent) => {
    const activatedChartId = event.metadata?.chartId || event.chartId;
    // If a different chart is activated while overlay is open, close it
    if (activatedChartId && activatedChartId !== chartId && state.type !== 'hidden') {
      if (DEBUG) devLog(`[PlanetDrag:Overlay] Closing - another chart activated: ${activatedChartId}`);
      stopHold();
      setWaitingDirection(null);
      setState({ type: 'hidden' });

      // Emit cancel to restore original planet positions
      globalEventStream.emit(createEvent<BaseEvent>(
        'planet.drag.cancel',
        'PlanetDragOverlay',
        { metadata: { chartId } }
      ));

      // Emit overlay closed event
      globalEventStream.emit(createEvent<BaseEvent>(
        'planet.drag.overlay.closed',
        'PlanetDragOverlay',
        { metadata: { chartId } }
      ));
    }
  }, [chartId, state.type, stopHold]);

  // Update step when Movement settings change - switch to loading state
  const handleMovementStepChanged = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;
    if (state.type !== 'navigating') return;

    const planet = event.metadata?.planet as string;
    const stepDegrees = event.metadata?.stepDegrees as number;

    // Only update if the planet matches current drag
    if (planet === state.planet && stepDegrees !== undefined) {
      if (DEBUG) devLog(`[PlanetDrag:Overlay] Step changed: ${state.stepDegrees}° → ${stepDegrees}°, showing loading`);
      // Switch to loading state while buffer reloads
      setState({ type: 'loading', planet, progress: 0 });
    }
  }, [chartId, state]);

  // Handle motion state query - respond if we're active
  const handleMotionStateQuery = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;
    if (state.type === 'hidden') return;

    if (DEBUG) devLog(`[PlanetDrag:Overlay] Responding to motion.state.query`);
    globalEventStream.emit(createEvent<BaseEvent>(
      'motion.state.response',
      'PlanetDragOverlay',
      {
        metadata: {
          chartId,
          motionType: 'planet-drag',
          planet: state.type === 'navigating' ? state.planet : (state.type === 'loading' ? state.planet : null),
        },
      }
    ));
  }, [chartId, state]);

  // ===========================================================================
  // Effect: Subscribe to events
  // ===========================================================================

  useEffect(() => {
    const subscriptions: EventSubscription[] = [
      globalEventStream.subscribe('planet.drag.start', handleDragStart),
      globalEventStream.subscribe('planet.drag.progress', handleDragProgress),
      globalEventStream.subscribe('planet.drag.ready', handleDragReady),
      globalEventStream.subscribe('planet.drag.navigation.update', handleNavigationUpdate),
      globalEventStream.subscribe('planet.drag.end', handleDragEnd),
      globalEventStream.subscribe('planet.drag.cancel', handleDragCancel),
      globalEventStream.subscribe('chart:activated', handleChartActivated),
      globalEventStream.subscribe('planet.movement.step.changed', handleMovementStepChanged),
      globalEventStream.subscribe('motion.state.query', handleMotionStateQuery),
    ];

    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());
      stopHold();
    };
  }, [handleDragStart, handleDragProgress, handleDragReady, handleNavigationUpdate, handleDragEnd, handleDragCancel, handleChartActivated, handleMovementStepChanged, handleMotionStateQuery, stopHold]);

  // ===========================================================================
  // Update foreignObject pointer-events based on visibility
  // ===========================================================================

  useEffect(() => {
    if (!portalTarget) return;

    // Find the parent foreignObject
    const foreignObject = portalTarget.closest('foreignObject');
    if (foreignObject) {
      // Enable pointer events when overlay is visible
      foreignObject.setAttribute('pointer-events', state.type !== 'hidden' ? 'auto' : 'none');
      portalTarget.style.pointerEvents = state.type !== 'hidden' ? 'auto' : 'none';
    }
  }, [state.type, portalTarget]);

  // ===========================================================================
  // Render
  // ===========================================================================

  // Don't render if hidden or no portal target
  if (state.type === 'hidden') {
    return null;
  }

  // Render content
  const renderContent = () => {
    if (state.type === 'loading') {
      return (
        <div className="planet-drag-overlay planet-drag-overlay--loading planet-drag-overlay--in-svg">
          <div className="planet-drag-overlay__content">
            <div className="planet-drag-overlay__spinner" />
            <span className="planet-drag-overlay__text">
              Loading {state.planet} buffer...
            </span>
            {state.progress > 0 && (
              <div className="planet-drag-overlay__progress">
                <div
                  className="planet-drag-overlay__progress-bar"
                  style={{ width: `${state.progress}%` }}
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (state.type === 'navigating') {
      const hasChanged = state.originalLongitude !== null && state.planetPosition?.longitude !== state.originalLongitude;

      return (
        <div className="planet-drag-overlay planet-drag-overlay--navigating planet-drag-overlay--in-svg">
          <div className="planet-drag-overlay__nav-container">
            {/* Back button */}
            <NavButton
              direction="back"
              disabled={!state.canGoBack}
              waiting={waitingDirection === 'back'}
              isPartial={state.isPartial ?? false}
              onNavigate={() => startHold('back')}
              onDisabledClick={() => handleDisabledClick('back')}
              onStop={stopHold}
            />

            {/* Content display */}
            <div className="planet-drag-overlay__time-display">
              {/* Step info */}
              <span className="planet-drag-overlay__planet">
                {state.planet} Step: {state.stepDegrees}°
              </span>

              {/* Offset indicator */}
              <span className="planet-drag-overlay__coordinates">
                {formatDegreeOffset(state.degreeOffset)}
              </span>

              {/* Buffer position indicator - use actual buffer indices for correct display */}
              <BufferIndicator
                currentIndex={state.currentIndex}
                centerIndex={state.centerIndex}
                bufferSize={DEGREE_BUFFER_SIZE}
                loadingProgress={state.loadingProgress ?? 100}
              />

              {/* Planet position */}
              {state.planetPosition && (
                <>
                  <span className="planet-drag-overlay__rashi">
                    {formatLocalDegrees(state.planetPosition.degreesInRashi)} {RASHI_NAMES[(state.planetPosition.rashiIndex + 1) % 12] || ''}
                  </span>
                </>
              )}

              {/* Time and date */}
              <span className="planet-drag-overlay__time">
                {formatTime(state.currentTime, state.timezoneCorrection)}
              </span>
              <span className="planet-drag-overlay__date">
                {formatDate(state.currentTime, state.timezoneCorrection)}
              </span>
            </div>

            {/* Forward button */}
            <NavButton
              direction="forward"
              disabled={!state.canGoForward}
              waiting={waitingDirection === 'forward'}
              isPartial={state.isPartial ?? false}
              onNavigate={() => startHold('forward')}
              onDisabledClick={() => handleDisabledClick('forward')}
              onStop={stopHold}
            />
          </div>

          {/* Action buttons */}
          <ActionButtons
            hasChanged={hasChanged}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
          />

          {/* Hint */}
          <NavigationHint />
        </div>
      );
    }

    return null;
  };

  // Use portal if target exists, otherwise render in place (fallback)
  const content = renderContent();
  if (!content) return null;

  if (portalTarget) {
    return createPortal(content, portalTarget);
  }

  // Fallback: render without portal (old behavior)
  return content;
};

export default PlanetDragOverlay;
